import React from 'react';
import { makeStyles, tokens, Title2, Title3, Body1, Card, CardHeader, Input, Button, Divider, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Badge, Select, Field } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { BUSINESS_UNIT_LEADERS, VolunteerStatus } from '../types/sharepoint';
// import { useNavigate } from 'react-router-dom';
import { getSharePointService } from '../services/sharePointService';
import type { Volunteer } from '../types/sharepoint';
import VolunteerDetailsModal from '../components/VolunteerDetailsModal';
import { useDepartmentDirectors } from '../hooks/useDepartmentDirectors';
import { useAuth } from '../hooks/useAuth';
import { AddRegular, DeleteRegular, PersonRegular } from '@fluentui/react-icons';
import SearchableDropdown from '../components/SearchableDropdown';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    justifyContent: 'center',
    textAlign: 'center',
    marginBottom: '8px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  controlRight: {
    display: 'flex',
    gap: '8px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '16px',
    alignItems: 'stretch',
  },
  deptCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  centerHeader: {
    textAlign: 'center',
    width: '100%',
  },
  teams: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
    padding: '12px 16px 16px',
  },
  teamItem: {
    padding: '4px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  teamHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
    padding: '6px 8px',
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  teamHeaderTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  teamHeaderBottom: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    paddingLeft: '4px',
  },
  leadName: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '13px',
  },
  teamMembers: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '16px',
    marginTop: '4px',
  },
  teamMember: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  teamMemberName: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightRegular,
  },
  expandIcon: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    marginLeft: '8px',
  },
  teamName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  chipRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '16px',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  clickableChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '16px',
    fontSize: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'center',
    padding: '12px 16px',
  },
  note: {
    color: tokens.colorNeutralForeground3,
  },
  roster: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '0 16px 16px',
  },
  rosterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '8px 10px',
    borderRadius: '8px',
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  rosterItemTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  rosterName: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  rosterMeta: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  rosterTeamInfo: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    paddingLeft: '4px',
  },
  rosterTeamName: {
    fontWeight: tokens.fontWeightSemibold,
  },
  rosterLeadName: {
    fontStyle: 'italic',
  },
  rosterRoleTag: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  rosterInactive: {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    border: `1px solid ${tokens.colorPaletteRedBackground2}`,
  },
  modalRosterList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '12px',
  },
  modalFooter: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  teamSection: {
    marginBottom: '20px',
  },
  teamSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    marginBottom: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  teamSectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  teamSectionCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  teamMembersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '16px',
  },
  teamsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  teamCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'fit-content',
    maxHeight: '400px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '8px',
  },
  teamCardContent: {
    padding: '12px',
    overflowY: 'auto',
    maxHeight: '320px',
    flex: 1,
  },
  compactMembersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  compactMemberItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '12px',
  },
  compactMemberName: {
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
    fontSize: '12px',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  unassignedSection: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  unassignedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  unassignedTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '16px',
    color: tokens.colorNeutralForeground1,
  },
  unassignedCount: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  unassignedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '10px',
  },
  unassignedMemberCard: {
    padding: '10px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: '12px',
  },
  sectionHeader: {
    marginTop: '24px',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: '20px',
    color: tokens.colorNeutralForeground1,
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
    marginBottom: '24px',
  },
  memberCard: {
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  directorCard: {
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  directorCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  directorCardName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: tokens.fontWeightSemibold,
  },
  directorCardDept: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

type OrgDept = {
  name: string;
  key: string; // key used to look up director via BUSINESS_UNIT_LEADERS
  director?: string;
  teams: string[];
};

const ORG: OrgDept[] = [
  {
    name: 'Marketing & Communications',
    key: 'Marketing and Communications',
    teams: ['Marketing/Comm Team', 'Community Engagement Team'],
  },
  {
    name: 'Clinical Operations',
    key: 'Clinical Operations',
    teams: ['Registration Team', 'Providers Team', 'Patient Advocate Team', 'Medical Services Team', 'Quality Team'],
  },
  {
    name: 'Finance & Development',
    key: 'Finance & Development',
    teams: ['Finance Team', 'Donor Development Team', 'Procurement Team', 'Tax/Audit Team'],
  },
  {
    name: 'Legal & HR',
    key: 'LEHR',
    teams: ['Legal/Compliance Team', 'Human Resources Team'],
  },
  {
    name: 'Support Services',
    key: 'Support Services',
    teams: ['Admin Team', 'SetUp / Church Liaison Team', 'Liaison/Greeter Team', 'Prayer Team'],
  },
  {
    name: 'Technology',
    key: 'Technology',
    teams: ['Systems Management Team', 'Technical Support Team', 'EHR Team'],
  },
];

const OrgChart: React.FC = () => {
  const styles = useStyles();
  // Note: navigate currently unused but kept for potential future deep-links
  // const navigate = useNavigate();
  const [search, setSearch] = React.useState('');
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  const [volunteers, setVolunteers] = React.useState<Volunteer[]>([]);
  const { hasRole } = useAuth();
  const canManageDirectors = hasRole(['admin', 'executive']);
  const { 
    directors: allDepartmentDirectors,
    getDirectorObjects,
    loading: loadingDirectors,
    refreshDirectors 
  } = useDepartmentDirectors();
  const [roleToLead, setRoleToLead] = React.useState<Record<string, string>>({});
  const [isRosterOpen, setIsRosterOpen] = React.useState(false);
  const [rosterDeptKey, setRosterDeptKey] = React.useState<string | null>(null);
  const [rosterOnlyNew, setRosterOnlyNew] = React.useState<boolean>(false);
  const [selectedVolunteer, setSelectedVolunteer] = React.useState<Volunteer | null>(null);
  const [expandedTeams, setExpandedTeams] = React.useState<Set<string>>(new Set());
  const [assigningLead, setAssigningLead] = React.useState<{ deptKey: string; team: string; role: string } | null>(null);
  const [managingDirectors, setManagingDirectors] = React.useState<{ deptKey: string } | null>(null);
  const [selectedDirectorForEdit, setSelectedDirectorForEdit] = React.useState<{ id: string; department: string; directorName: string } | null>(null);
  const [newDirectorName, setNewDirectorName] = React.useState('');
  const [newDirectorEmail, setNewDirectorEmail] = React.useState('');
  const [showConfirmRemove, setShowConfirmRemove] = React.useState(false);
  // Removed unused assigningTeam state

  // Local normalization mirroring types/sharepoint.ts
  const normalizeDepartment = React.useCallback((raw?: string): string | undefined => {
    if (!raw) return undefined;
    const map: Record<string, string> = {
      'Communications': 'Marketing and Communications',
      'Marketing & Communications': 'Marketing and Communications',
      'Marketing and Communications': 'Marketing and Communications',
      'Clinical': 'Clinical Operations',
      'Clinical Operations': 'Clinical Operations',
      'Clinical Operation': 'Clinical Operations',
      'Finance': 'Finance & Development',
      'Finance and Development': 'Finance & Development',
      'Finance & Development': 'Finance & Development',
      'Legal & HR (LEHR)': 'LEHR',
      'Legal & HR': 'LEHR',
      'LEHR': 'LEHR',
      'Operations': 'Support Services',
      'Operational': 'Support Services',
      'Care': 'Support Services',
      'Support Services': 'Support Services',
      'Technology': 'Technology',
    };
    return map[raw] || raw;
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const data = await getSharePointService().getVolunteers();
        setVolunteers(data);
        const map: Record<string, string> = {};
        data.forEach(v => {
          if (v.assignedRole && v.name) {
            // Prefer the first occurrence; later we could support multiples
            if (!map[v.assignedRole]) map[v.assignedRole] = v.name;
          }
        });
        setRoleToLead(map);
      } catch (e) {
        // Fail silent; UI will display TBD
      }
    })();
  }, []);

  // Build per-department totals (treat blank role as Volunteer; exclude Inactive)
  const deptVolunteerTotals = React.useMemo(() => {
    const totals: Record<string, number> = {};
    volunteers.forEach(v => {
      const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
      if (status === VolunteerStatus.INACTIVE) return;
      const role = (v.assignedRole || '').trim().toLowerCase();
      const isVolunteerRole = role === '' || role === 'volunteer' || role === 'volunteers' || role === 'general volunteer';
      if (!isVolunteerRole) return;
      const key = normalizeDepartment(v.businessUnit) || 'Unassigned';
      totals[key] = (totals[key] || 0) + 1;
    });
    return totals;
  }, [volunteers, normalizeDepartment]);

  // Helper: determine if a volunteer is NEW (joined within 90 days)
  const isVolunteerNew = React.useCallback((v: Volunteer): boolean => {
    try {
      if (!v.createdAt) return false;
      const created = new Date(v.createdAt as string);
      const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
      const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
      if (status === VolunteerStatus.INACTIVE) return false;
      return days <= 90;
    } catch { return false; }
  }, []);

  // Build per-department NEW volunteers counts (<= 90 days, excluding Inactive)
  const deptNewVolunteerCounts = React.useMemo(() => {
    const totals: Record<string, number> = {};
    volunteers.forEach(v => {
      const key = normalizeDepartment(v.businessUnit) || 'Unassigned';
      if (isVolunteerNew(v)) {
        totals[key] = (totals[key] || 0) + 1;
      }
    });
    return totals;
  }, [volunteers, normalizeDepartment, isVolunteerNew]);
  
  // Display helper: show roles as "Lead" rather than "Manager" while keeping underlying data unchanged
  const displayLeadRole = React.useCallback((role?: string): string | undefined => {
    if (!role) return role;
    return role.replace(/Manager\b/g, 'Lead');
  }, []);

  // Handle assigning a team lead
  const handleAssignLead = async (volunteerId: string) => {
    if (!assigningLead) return;
    
    try {
      const volunteer = volunteers.find(v => v.id === volunteerId);
      if (!volunteer) return;
      
      // Find the old lead who has this role and clear their role
      const oldLead = volunteers.find(v => 
        v.assignedRole === assigningLead.role && v.id !== volunteerId
      );
      
      // Clear old lead's role if exists
      if (oldLead) {
        await getSharePointService().updateVolunteer(oldLead.id, {
          assignedRole: ''
        });
      }
      
      // Update the volunteer's assignedRole to the team lead role
      await getSharePointService().updateVolunteer(volunteerId, {
        assignedRole: assigningLead.role
      });
      
      // Reload volunteers to ensure we have the latest data
      const updatedData = await getSharePointService().getVolunteers();
      setVolunteers(updatedData);
      
      // Rebuild roleToLead mapping from fresh data
      const map: Record<string, string> = {};
      updatedData.forEach(v => {
        if (v.assignedRole && v.name) {
          if (!map[v.assignedRole]) map[v.assignedRole] = v.name;
        }
      });
      setRoleToLead(map);
      
      setAssigningLead(null);
    } catch (error) {
      console.error('Error assigning department lead:', error);
      alert('Failed to assign department lead. Please try again.');
    }
  };

  // Get available volunteers for a department (excluding those already assigned to other lead roles)
  const getAvailableVolunteersForLead = React.useCallback((deptKey: string): Volunteer[] => {
    return volunteers.filter(v => {
      const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
      if (status === VolunteerStatus.INACTIVE) return false;
      
      const vDept = normalizeDepartment(v.businessUnit);
      if (vDept !== deptKey) return false;
      
      // Include volunteers who don't have a lead role, or who already have this role
      if (!v.assignedRole) return true;
      if (assigningLead && v.assignedRole === assigningLead.role) return true;
      
      // Exclude volunteers who already have a different lead role
      const isLeadRole = v.assignedRole.includes('Lead') || v.assignedRole.includes('Manager') || v.assignedRole.includes('Director');
      return !isLeadRole;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [volunteers, normalizeDepartment, assigningLead]);

  // Handle assigning a volunteer to a team
  const handleAssignToTeam = async (volunteerId: string, teamName: string) => {
    try {
      await getSharePointService().updateVolunteer(volunteerId, {
        secondaryArea: teamName
      });
      
      // Reload volunteers to ensure persistence
      const updatedData = await getSharePointService().getVolunteers();
      setVolunteers(updatedData);
    } catch (error) {
      console.error('Error assigning volunteer to team:', error);
      alert('Failed to assign volunteer to team. Please try again.');
    }
  };

  // Handle removing a volunteer from a team (unassigning)
  const handleUnassignFromTeam = async (volunteerId: string) => {
    try {
      await getSharePointService().updateVolunteer(volunteerId, {
        secondaryArea: ''
      });
      
      // Reload volunteers to ensure persistence
      const updatedData = await getSharePointService().getVolunteers();
      setVolunteers(updatedData);
    } catch (error) {
      console.error('Error unassigning volunteer from team:', error);
      alert('Failed to remove volunteer from team. Please try again.');
    }
  };

  // Build per-department roster (all volunteers regardless of status/role)
  const deptRoster = React.useMemo(() => {
    const map: Record<string, Volunteer[]> = {};
    volunteers.forEach(v => {
      const key = normalizeDepartment(v.businessUnit) || 'Unassigned';
      if (!map[key]) map[key] = [];
      map[key].push(v);
    });
    // Sort each department roster alphabetically by name for easier scanning
    Object.keys(map).forEach(k => {
      map[k] = map[k].slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
    return map;
  }, [volunteers, normalizeDepartment]);

  // Build per-team roster (volunteers assigned to each team via secondaryArea)
  const teamRoster = React.useMemo(() => {
    const map: Record<string, Volunteer[]> = {};
    
    // Build a map of all teams by department key
    const teamsByDept: Record<string, string[]> = {};
    ORG.forEach(dept => {
      teamsByDept[dept.key] = dept.teams;
    });
    
    volunteers.forEach(v => {
      const deptKey = normalizeDepartment(v.businessUnit);
      if (!deptKey) return;
      
      const deptTeams = teamsByDept[deptKey] || [];
      
      // Match volunteer's secondaryArea to team names - STRICT MATCHING ONLY
      const team = deptTeams.find(t => {
        const volunteerTeam = (v.secondaryArea || '').trim();
        // Only exact match (case-insensitive)
        if (!volunteerTeam) return false;
        return volunteerTeam.toLowerCase() === t.toLowerCase();
      });
      
      if (team) {
        const teamKey = `${deptKey}::${team}`;
        if (!map[teamKey]) map[teamKey] = [];
        map[teamKey].push(v);
      }
    });
    
    // Sort each team roster alphabetically by name
    Object.keys(map).forEach(k => {
      map[k] = map[k].slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    });
    return map;
  }, [volunteers, normalizeDepartment]);
  const managerRoleForTeam: Record<string, Record<string, string>> = {
    'Marketing and Communications': {
      'Marketing/Comm Team': 'Marketing/Comm Lead',
      'Community Engagement Team': 'Community Engagement Lead',
    },
    'Clinical Operations': {
      'Registration Team': 'Registration Lead',
      'Providers Team': 'Providers Lead',
      'Patient Advocate Team': 'Patient Advocate Lead',
      'Medical Services Team': 'Medical Services Lead',
      'Quality Team': 'Quality Lead',
    },
    'Finance & Development': {
      'Finance Team': 'Finance Lead',
      'Donor Development Team': 'Donor Development Lead',
      'Procurement Team': 'Procurement Lead',
      'Tax/Audit Team': 'Tax/Audit Lead',
    },
    'LEHR': {
      'Legal/Compliance Team': 'Legal/Compliance Lead',
      'Human Resources Team': 'Human Resource Lead',
    },
    'Support Services': {
      'Admin Team': 'Admin Lead',
      'SetUp / Church Liaison Team': 'SetUp / Church Liaison Lead',
      'Liaison/Greeter Team': 'Liaison/Greeter Lead',
      'Prayer Team': 'Prayer Lead',
    },
    'Technology': {
      'Systems Management Team': 'Systems Management Lead',
      'Technical Support Team': 'Technical Support Lead',
      'EHR Team': 'EHR Lead',
    },
  };
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Organization Chart</Title2>
      </div>

      <div className={styles.controls}>
        <Input 
          placeholder="Search departments or teams..." 
          value={search}
          onChange={(_, data) => setSearch(data.value)}
        />
        <div className={styles.controlRight}>
          <Button appearance="secondary" onClick={() => setCollapsed(new Set(ORG.map(d => d.key)))}>Collapse All</Button>
          <Button appearance="primary" onClick={() => setCollapsed(new Set())}>Expand All</Button>
        </div>
      </div>

      {/* Board Members Section */}
      {(() => {
        const boardMembers = volunteers.filter(v => {
          const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
          return v.assignedRole === 'Board Member' && status !== VolunteerStatus.INACTIVE;
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        
        if (boardMembers.length === 0) return null;
        
        return (
          <div>
            <div className={styles.sectionHeader}>
              <Title3 className={styles.sectionTitle}>Board Members</Title3>
            </div>
            <div className={styles.sectionGrid}>
              {boardMembers.map((member) => (
                <div
                  key={member.id}
                  className={styles.memberCard}
                  onClick={() => setSelectedVolunteer(member)}
                >
                  <PersonRegular style={{ fontSize: '16px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold }}>{member.name}</div>
                    {member.email && (
                      <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                        {member.email}
                      </div>
                    )}
                  </div>
                  <Badge size="small" appearance="filled">Board</Badge>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Department Directors Section */}
      {(() => {
        const directorsByDept: Record<string, typeof allDepartmentDirectors> = {};
        
        allDepartmentDirectors.forEach(dir => {
          if (!directorsByDept[dir.department]) {
            directorsByDept[dir.department] = [];
          }
          directorsByDept[dir.department].push(dir);
        });
        
        if (allDepartmentDirectors.length === 0) return null;
        
        return (
          <div>
            <div className={styles.sectionHeader}>
              <Title3 className={styles.sectionTitle}>Department Directors</Title3>
            </div>
            <div className={styles.sectionGrid}>
              {Object.entries(directorsByDept).sort(([a], [b]) => a.localeCompare(b)).map(([dept, directors]) => (
                <div key={dept} className={styles.directorCard}>
                  <div className={styles.directorCardHeader}>
                    <div className={styles.directorCardName}>
                      <PersonRegular style={{ fontSize: '16px' }} />
                      <span>{dept}</span>
                    </div>
                    {canManageDirectors && (
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<AddRegular />}
                        onClick={() => {
                          const deptKey = ORG.find(d => d.name === dept)?.key || dept;
                          setManagingDirectors({ deptKey });
                          setNewDirectorName('');
                          setNewDirectorEmail('');
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingLeft: '24px' }}>
                    {directors.map((dir) => {
                      const isBoardMember = volunteers.find(v => v.name === dir.directorName)?.assignedRole === 'Board Member';
                      return (
                        <div
                          key={dir.id}
                          className={styles.memberCard}
                          style={{ 
                            padding: '8px 12px',
                            cursor: canManageDirectors ? 'pointer' : 'default',
                            backgroundColor: isBoardMember 
                              ? tokens.colorBrandBackground2 
                              : tokens.colorNeutralBackground2,
                          }}
                          onClick={canManageDirectors ? () => {
                            setSelectedDirectorForEdit({ id: dir.id, department: dir.department, directorName: dir.directorName });
                          } : undefined}
                        >
                          <PersonRegular style={{ fontSize: '14px' }} />
                          <div style={{ flex: 1, fontSize: '13px' }}>{dir.directorName}</div>
                          {isBoardMember && (
                            <Badge size="small" appearance="filled">Board</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Departments Section */}
      <div className={styles.sectionHeader}>
        <Title3 className={styles.sectionTitle}>Departments & Teams</Title3>
      </div>

      <div className={styles.grid}>
        {ORG.filter(dept => {
          const term = search.trim().toLowerCase();
          if (!term) return true;
          const matchesDept = dept.name.toLowerCase().includes(term);
          const matchesTeam = dept.teams.some(t => t.toLowerCase().includes(term));
          return matchesDept || matchesTeam;
        }).map((dept) => {
          // Get all directors for this department from SharePoint
          const departmentDirectors = loadingDirectors 
            ? []
            : getDirectorObjects(dept.key);
          const fallbackDirector = BUSINESS_UNIT_LEADERS[dept.key] || 'TBD';
          
          // Helper to check if a director volunteer is a Board Member
          const isDirectorBoardMember = (directorName: string): boolean => {
            const volunteer = volunteers.find(v => v.name === directorName);
            return volunteer?.assignedRole === 'Board Member' || false;
          };
          
          const totalVolunteers = deptVolunteerTotals[dept.key] || 0;
          const isCollapsed = collapsed.has(dept.key);
          return (
            <Card key={dept.key} className={styles.deptCard}>
              <CardHeader
                className={styles.cardHeader}
                header={<Title3 className={styles.centerHeader}>{dept.name}</Title3>}
                onClick={() => {
                  const next = new Set(collapsed);
                  if (next.has(dept.key)) next.delete(dept.key); else next.add(dept.key);
                  setCollapsed(next);
                }}
              />
              {!isCollapsed && (
              <div className={styles.teams}>
                {/* Meta row: Directors, Volunteers count, and actions */}
                <div className={styles.chipWrap}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                    <div style={{ fontSize: '11px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase' }}>
                      Department Directors:
                    </div>
                    {loadingDirectors ? (
                      <div className={styles.chip}>Loading...</div>
                    ) : departmentDirectors.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {departmentDirectors.map((dir) => {
                          const isBoardMember = isDirectorBoardMember(dir.directorName);
                          return (
                            <div
                              key={dir.id}
                              className={isBoardMember ? styles.clickableChip : styles.chip}
                              style={{
                                backgroundColor: isBoardMember 
                                  ? tokens.colorBrandBackground2 
                                  : tokens.colorNeutralBackground2,
                                cursor: canManageDirectors ? 'pointer' : 'default',
                              }}
                              onClick={canManageDirectors ? (e) => {
                                e.stopPropagation();
                                setSelectedDirectorForEdit({ id: dir.id, department: dir.department, directorName: dir.directorName });
                              } : undefined}
                              title={isBoardMember ? `${dir.directorName} (Board Member)${canManageDirectors ? ' - Click to edit' : ''}` : dir.directorName}
                            >
                              <PersonRegular style={{ fontSize: '12px' }} />
                              <span>{dir.directorName}</span>
                              {isBoardMember && (
                                <Badge size="small" appearance="filled" style={{ marginLeft: '4px' }}>
                                  Board
                                </Badge>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={styles.chip} style={{ color: tokens.colorNeutralForeground3 }}>
                        {fallbackDirector} {fallbackDirector !== 'TBD' && '(Legacy)'}
                      </div>
                    )}
                    {canManageDirectors && (
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<AddRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setManagingDirectors({ deptKey: dept.key });
                          setNewDirectorName('');
                          setNewDirectorEmail('');
                        }}
                        style={{ marginTop: '4px', alignSelf: 'flex-start' }}
                      >
                        Add Director
                      </Button>
                    )}
                  </div>
                  <div className={styles.chip} title="Volunteers in this department">
                    Volunteers: {totalVolunteers}
                  </div>
                  <Button
                    appearance="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRosterDeptKey(dept.key);
                      setRosterOnlyNew(true);
                      setIsRosterOpen(true);
                    }}
                    disabled={(deptNewVolunteerCounts[dept.key] || 0) === 0}
                  >
                    New Volunteers ({deptNewVolunteerCounts[dept.key] || 0})
                  </Button>
                  <Button
                    appearance="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRosterDeptKey(dept.key);
                      setRosterOnlyNew(false);
                      setIsRosterOpen(true);
                    }}
                  >
                    View Members
                  </Button>
                </div>
                <Divider />
                {/* Lead chips with team members (display only; underlying roles unchanged) */}
                {dept.teams.map((team) => {
                  const role = managerRoleForTeam[dept.key]?.[team];
                  const leadName = role ? roleToLead[role] : undefined;
                  const teamKey = `${dept.key}::${team}`;
                  const teamMembers = teamRoster[teamKey] || [];
                  // Filter out inactive members and managers from team member list
                  const activeTeamMembers = teamMembers.filter(v => {
                    const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
                    if (status === VolunteerStatus.INACTIVE) return false;
                    // Don't show the manager/lead in their own team list
                    if (v.assignedRole === role) return false;
                    return true;
                  });
                  const isExpanded = expandedTeams.has(teamKey);
                  const memberCount = activeTeamMembers.length;
                  
                  return (
                    <div key={team} className={styles.teamItem}>
                      <div 
                        className={styles.teamHeader}
                        onClick={() => {
                          const next = new Set(expandedTeams);
                          if (next.has(teamKey)) next.delete(teamKey); else next.add(teamKey);
                          setExpandedTeams(next);
                        }}
                      >
                        <div className={styles.teamHeaderTop}>
                          <div className={styles.chipRow}>
                            <div className={styles.chip}>
                              {role ? displayLeadRole(role) : 'Lead'}
                            </div>
                            {memberCount > 0 && (
                              <div className={styles.chip} style={{ fontSize: '11px' }}>
                                {memberCount} {memberCount === 1 ? 'member' : 'members'}
                              </div>
                            )}
                          </div>
                          <span className={styles.expandIcon}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                        <div className={styles.teamHeaderBottom}>
                          <span 
                            className={styles.leadName}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (role) {
                                setAssigningLead({ deptKey: dept.key, team, role });
                              }
                            }}
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            title="Click to assign department lead"
                          >
                            {leadName || 'TBD'}
                          </span>
                        </div>
                      </div>
                      {isExpanded && memberCount > 0 && (
                        <div className={styles.teamMembers}>
                          {activeTeamMembers.map(v => {
                            const isNew = isVolunteerNew(v);
                            return (
                              <div 
                                key={v.id} 
                                className={styles.teamMember}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedVolunteer(v);
                                }}
                              >
                                <span className={styles.teamMemberName}>
                                  {v.name || 'Unknown'}
                                  {isNew && (
                                    <Badge appearance="filled" color="success" style={{ marginLeft: 6, fontSize: '10px' }}>NEW</Badge>
                                  )}
                                </span>
                                {(v.volunteerStatus === VolunteerStatus.INACTIVE || v.status === VolunteerStatus.INACTIVE) && (
                                  <span className={styles.rosterInactive} style={{ fontSize: '10px' }}>Inactive</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {isExpanded && memberCount === 0 && (
                        <div className={styles.teamMembers}>
                          <Body1 className={styles.note} style={{ fontSize: '11px', paddingLeft: '8px' }}>
                            No members assigned to this team yet.
                          </Body1>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Roster Modal */}
      {isRosterOpen && rosterDeptKey && (() => {
        const deptMembers = (deptRoster[rosterDeptKey] || []).filter(v => rosterOnlyNew ? isVolunteerNew(v) : true);
        const deptTeams = ORG.find(d => d.key === rosterDeptKey)?.teams || [];
        
        // Group members by team
        const membersByTeam: Record<string, Volunteer[]> = {};
        const unassignedMembers: Volunteer[] = [];
        
        deptMembers.forEach(v => {
          const volunteerTeam = (v.secondaryArea || '').trim();
          // STRICT MATCHING ONLY - must exactly match team name (case-insensitive)
          const matchedTeam = deptTeams.find(t => {
            if (!volunteerTeam) return false;
            return volunteerTeam.toLowerCase() === t.toLowerCase();
          });
          
          if (matchedTeam) {
            if (!membersByTeam[matchedTeam]) membersByTeam[matchedTeam] = [];
            membersByTeam[matchedTeam].push(v);
          } else {
            unassignedMembers.push(v);
          }
        });
        
        return (
          <Dialog open onOpenChange={() => setIsRosterOpen(false)} modalType="non-modal">
            <DialogSurface style={{ width: '95vw', maxWidth: '1800px', height: '95vh', maxHeight: '1000px' }}>
              <DialogBody style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <DialogTitle>
                  {rosterOnlyNew ? `New Volunteers — ${rosterDeptKey}` : `Department Members — ${rosterDeptKey}`}
                </DialogTitle>
                <DialogContent style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Teams in grid layout */}
                  <div className={styles.teamsGrid}>
                    {deptTeams.map(team => {
                      const teamMembers = membersByTeam[team] || [];
                      if (teamMembers.length === 0 && rosterOnlyNew) return null;
                      
                      const role = managerRoleForTeam[rosterDeptKey]?.[team];
                      const leadName = role ? roleToLead[role] : undefined;
                      
                      return (
                        <Card key={team} className={styles.teamCard}>
                          <CardHeader
                            header={
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                <div>
                                  <div style={{ fontWeight: tokens.fontWeightSemibold, fontSize: '15px', color: tokens.colorNeutralForeground1 }}>
                                    {team}
                                  </div>
                                  <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3, marginTop: '2px' }}>
                                    {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
                                  </div>
                                </div>
                                <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground2, textAlign: 'right' }}>
                                  {leadName ? (
                                    <span>Lead: {leadName}</span>
                                  ) : (
                                    <span style={{ fontStyle: 'italic', color: tokens.colorNeutralForeground3 }}>Lead: TBD</span>
                                  )}
                                </div>
                              </div>
                            }
                          />
                          <div className={styles.teamCardContent}>
                            {teamMembers.length > 0 ? (
                              <div className={styles.compactMembersList}>
                                {teamMembers.map(v => {
                                  let isNew = false;
                                  try {
                                    if (v.createdAt) {
                                      const created = new Date(v.createdAt as string);
                                      const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                                      isNew = days <= 90;
                                    }
                                  } catch {}
                                  
                                  return (
                                    <div key={v.id} className={styles.compactMemberItem}>
                                      <span 
                                        className={styles.compactMemberName}
                                        onClick={() => setSelectedVolunteer(v)}
                                        style={{ cursor: 'pointer', flex: 1 }}
                                      >
                                        {v.name || 'Unknown'}
                                        {isNew && (
                                          <Badge appearance="filled" color="success" style={{ marginLeft: 6, fontSize: '9px', padding: '1px 4px' }}>NEW</Badge>
                                        )}
                                      </span>
                                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                        {v.assignedRole && (
                                          <span className={styles.rosterRoleTag} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                            {displayLeadRole(v.assignedRole)}
                                          </span>
                                        )}
                                        {(v.volunteerStatus === VolunteerStatus.INACTIVE || v.status === VolunteerStatus.INACTIVE) && (
                                          <span className={styles.rosterInactive} style={{ fontSize: '10px', padding: '2px 6px' }}>Inactive</span>
                                        )}
                                        <Button
                                          icon={<DismissRegular />}
                                          size="small"
                                          appearance="subtle"
                                          onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleUnassignFromTeam(v.id);
                                          }}
                                          aria-label="Remove from team"
                                          style={{ minWidth: '24px', height: '24px', padding: '0' }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <Body1 className={styles.note} style={{ fontSize: '11px', color: tokens.colorNeutralForeground3, padding: '8px' }}>
                                No members assigned to this team yet.
                              </Body1>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                  
                  {/* Unassigned members at bottom */}
                  {unassignedMembers.length > 0 && (
                    <div className={styles.unassignedSection}>
                      <div className={styles.unassignedHeader}>
                        <span className={styles.unassignedTitle}>Unassigned</span>
                        <span className={styles.unassignedCount}>
                          ({unassignedMembers.length} {unassignedMembers.length === 1 ? 'member' : 'members'})
                        </span>
                      </div>
                      <div className={styles.unassignedGrid}>
                        {unassignedMembers.map(v => {
                          let isNew = false;
                          try {
                            if (v.createdAt) {
                              const created = new Date(v.createdAt as string);
                              const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                              isNew = days <= 90;
                            }
                          } catch {}
                          
                          return (
                            <div key={v.id} className={styles.unassignedMemberCard}>
                              {/* Row 1: Name */}
                              <div style={{ marginBottom: '6px' }}>
                                <span 
                                  className={styles.compactMemberName}
                                  onClick={() => setSelectedVolunteer(v)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {v.name || 'Unknown'}
                                  {isNew && (
                                    <Badge appearance="filled" color="success" style={{ marginLeft: 6, fontSize: '9px', padding: '1px 4px' }}>NEW</Badge>
                                  )}
                                </span>
                              </div>
                              {/* Row 2: Assign to team dropdown and role tags */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'space-between' }}>
                                <Select
                                  value=""
                                  onChange={(e, data) => {
                                    if (data.value) {
                                      handleAssignToTeam(v.id, data.value);
                                    }
                                  }}
                                  style={{ minWidth: '120px', maxWidth: '140px', fontSize: '10px', height: '24px' }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="">Assign to team...</option>
                                  {deptTeams.map(team => (
                                    <option key={team} value={team}>{team}</option>
                                  ))}
                                </Select>
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  {v.assignedRole && (
                                    <span className={styles.rosterRoleTag} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                      {displayLeadRole(v.assignedRole)}
                                    </span>
                                  )}
                                  {(v.volunteerStatus === VolunteerStatus.INACTIVE || v.status === VolunteerStatus.INACTIVE) && (
                                    <span className={styles.rosterInactive} style={{ fontSize: '10px', padding: '2px 6px' }}>Inactive</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {deptMembers.length === 0 && (
                    <Body1 className={styles.note} style={{ textAlign: 'center', padding: '40px' }}>No matching members.</Body1>
                  )}
                </DialogContent>
                <div className={styles.modalFooter} style={{ padding: '16px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                  <Button appearance="primary" onClick={() => setIsRosterOpen(false)}>Close</Button>
                </div>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        );
      })()}

      {selectedVolunteer && (
        <VolunteerDetailsModal
          volunteer={selectedVolunteer}
          onClose={() => setSelectedVolunteer(null)}
          onUpdated={(v) => {
            // Update volunteers state so chips and roster reflect changes immediately
            setVolunteers(prev => prev.map(p => (p.id === v.id ? { ...p, ...v } : p)));
            // Update roleToLead if role changed
            if (v.assignedRole) {
              setRoleToLead(prev => ({
                ...prev,
                [v.assignedRole!]: v.name
              }));
            }
          }}
        />
      )}

      {/* Assign Team Lead Dialog */}
      {assigningLead && (
        <Dialog open onOpenChange={() => setAssigningLead(null)}>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                Assign Department Lead: {assigningLead.team}
              </DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px 0' }}>
                  <Field label="Select a volunteer to assign as department lead">
                    <Select
                      value=""
                      onChange={(e, data) => {
                        if (data.value) {
                          handleAssignLead(data.value);
                        }
                      }}
                    >
                      <option value="">Select volunteer...</option>
                      {getAvailableVolunteersForLead(assigningLead.deptKey).map(v => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.assignedRole ? `(${v.assignedRole})` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {getAvailableVolunteersForLead(assigningLead.deptKey).length === 0 && (
                    <Body1 className={styles.note}>
                      No available volunteers in this department. Make sure volunteers are assigned to this department and are not already leads.
                    </Body1>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAssigningLead(null)}>Cancel</Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      <Body1 className={styles.note}>
        Teams are led by managers who report to the Department Directors. Click on a department lead name (or TBD) to assign a volunteer as the lead.
        {canManageDirectors && (
          <span> Board Members can manage department directors by clicking on director names or using the "Add Director" button.</span>
        )}
      </Body1>

      {/* Add Director Dialog */}
    {managingDirectors && (
      <Dialog open onOpenChange={(_, data) => {
        if (data.open === false) {
          setManagingDirectors(null);
          setNewDirectorName('');
          setNewDirectorEmail('');
        }
      }}>
        <DialogSurface style={{ minWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>Add Department Director</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
                <Field label="Department">
                  <div style={{ padding: '8px 12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                    {ORG.find(d => d.key === managingDirectors?.deptKey)?.name || managingDirectors?.deptKey}
                  </div>
                </Field>
                <Field label="Director Name" required>
                  <SearchableDropdown
                    label=""
                    options={volunteers
                      .filter(v => {
                        const status = (v.volunteerStatus || v.status) as VolunteerStatus | undefined;
                        return status !== VolunteerStatus.INACTIVE;
                      })
                      .map(v => ({ 
                        id: v.id, 
                        name: v.name, 
                        value: v.name, 
                        label: `${v.name}${v.assignedRole ? ` (${v.assignedRole})` : ''}` 
                      }))}
                    value={newDirectorName}
                    onChange={(value) => setNewDirectorName(value)}
                    placeholder="Search volunteers..."
                  />
                </Field>
                <Field label="Director Email (optional)">
                  <Input
                    type="email"
                    value={newDirectorEmail}
                    onChange={(e, data) => setNewDirectorEmail(data.value)}
                    placeholder="Enter email address"
                  />
                </Field>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, fontStyle: 'italic' }}>
                  Tip: Search for volunteers by name to assign an existing volunteer as director.
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setManagingDirectors(null);
                  setNewDirectorName('');
                  setNewDirectorEmail('');
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={async () => {
                  if (!newDirectorName.trim() || !managingDirectors) return;
                  try {
                    const service = getSharePointService();
                    await service.addDepartmentDirector(
                      managingDirectors.deptKey,
                      newDirectorName.trim(),
                      newDirectorEmail.trim() || undefined
                    );
                    // Wait a moment for SharePoint to process
                    await new Promise(resolve => setTimeout(resolve, 500));
                    // Refresh directors
                    await refreshDirectors();
                    setManagingDirectors(null);
                    setNewDirectorName('');
                    setNewDirectorEmail('');
                  } catch (error) {
                    console.error('Error adding director:', error);
                    alert('Failed to add director. Please try again.');
                  }
                }}
                disabled={!newDirectorName.trim()}
              >
                Add Director
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    )}

    {/* Edit/Remove Director Dialog */}
    {selectedDirectorForEdit && (
      <Dialog open onOpenChange={(_, data) => {
        if (data.open === false) {
          setSelectedDirectorForEdit(null);
        }
      }}>
        <DialogSurface style={{ minWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Manage Director</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
                <Field label="Department">
                  <div style={{ padding: '8px 12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                    {selectedDirectorForEdit?.department}
                  </div>
                </Field>
                <Field label="Director Name">
                  <div style={{ padding: '8px 12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                    {selectedDirectorForEdit?.directorName}
                  </div>
                </Field>
                <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                  This director is assigned to {selectedDirectorForEdit?.department}. You can remove them if needed.
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setSelectedDirectorForEdit(null)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<DeleteRegular />}
                onClick={() => {
                  if (!selectedDirectorForEdit) return;
                  setShowConfirmRemove(true);
                }}
              >
                Remove Director
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    )}

    {/* Confirm Remove Director Dialog */}
    {showConfirmRemove && selectedDirectorForEdit && (
      <Dialog open onOpenChange={(_, data) => {
        if (data.open === false) {
          setShowConfirmRemove(false);
        }
      }}>
        <DialogSurface style={{ minWidth: '400px' }}>
          <DialogBody>
            <DialogTitle>Confirm Removal</DialogTitle>
            <DialogContent>
              <Body1>
                Are you sure you want to remove {selectedDirectorForEdit.directorName} as director of {selectedDirectorForEdit.department}?
              </Body1>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setShowConfirmRemove(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<DeleteRegular />}
                onClick={async () => {
                  if (!selectedDirectorForEdit) return;
                  try {
                    const service = getSharePointService();
                    await service.removeDepartmentDirector(selectedDirectorForEdit.id);
                    await refreshDirectors();
                    setSelectedDirectorForEdit(null);
                    setShowConfirmRemove(false);
                  } catch (error) {
                    console.error('Error removing director:', error);
                    alert('Failed to remove director. Please try again.');
                  }
                }}
              >
                Remove Director
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    )}
    </div>
  );
};

export default OrgChart;


